import { createContext } from "react";

export type Workstream = {
    id : number,
    name : string,
    projectId : number,
    active : boolean,
    workPackages : WorkPackage[],
}

export type WorkPackage = {
    id : number,
    name : string,
    // active : boolean,
    startDate : Date,
    finishDate : Date,
    ownerId : string,
    index : number,
    tasks   : Task[],
}

export type Task = {
    id : number,
    name : string,
    // description : string,
    index : number,
    workPackageId : number,
    duration : number,
    startDate : Date,
    finishDate: Date,
    ownerId : string,
    completed: boolean,
    completedDate: Date | null,
    isDeliverable : boolean,
    isMilestone : boolean,
    displayOnCharter : boolean,
    displayOnStatusReport : boolean,
    dependencies : number[],
    hasDependents : boolean,
    level? : number, // Optional property for task level if it is a subtask
    status : 1|2|4,
    parentTaskId?: number, // Optional property for parent task ID
    subTasks?: Task[], // Optional property for subtasks
    isMoving?: boolean, // Optional property to indicate if the task is being moved
}

export interface TaskForTable extends Task {
    workstream : string,
    workPackage : string,
    ownerUsername : string,
    rating : string,
    hasDependencies : boolean,
}

interface PlanBuilderContextProps {
    projectStartDate: Date;
    updateProjectStartDate: (startDate: Date) => void;
    projectLifecycleStage: string;
    workstreams: Workstream[];
    selectedWorkstreamID: number | null;
    selectedWorkpackageID: number | null;
    selectedTaskID: number | null;
    selectedTaskIDs: number[];
    openWorkstreams: number[] | null;
    openWorkpackages: number[] | null;
    multiSelect: boolean;
    setOpenWorkstreams: (workstreamIDs: number[] | null) => void;
    selectWorkstream: (workstreamID: number) => void;
    selectWorkPackage: (workpackageID: number) => void;
    selectTask: (taskID: number, workPackageId? : number, event? : React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    selectMultiTask: (taskID: number, event? : React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    //addWorkstream: (workstreamName: string) => void;
    setWorkstreams: (workstreams: Workstream[]) => void;
    addWorkstream: () => void;
    updateWorkstream: (workstreamID: number, workstream: Workstream) => void;
    updateWorkPackage: (workpackage: WorkPackage) => void;
    deleteWorkstream: (workstreamID: number) => void;
    addWorkPackage: () => void;
    deleteWorkPackage: (workpackageID: number) => void;
    confirmDeleteWorkPackage: (workpackageID: number) => void;
    addTask: (workpackageID: number, task?: Task) => void;
    getTaskById : (taskID: number) => Task | undefined;
    getWorkPackageById: (workpackageID: number) => WorkPackage | undefined;
    getAllTasks: () => Task[];
    getDependentTaskIds: (taskID: number, dependentTaskIds: number[]) => void;
    updateTask: (taskID: number, task: Task) => void;
    groupEditTasks : (taskGroupEdit: TaskGroupEdit) => void;
    toggleWorkstream: (workstreamID: number) => void;
    toggleWorkPackage: (workpackageID: number) => void;
    setShowEditForm: (value: React.SetStateAction<boolean>) => void;
    deleteTask: (taskID: number) => void;
    groupDeleteTasks: (taskIDs: number[]) => void;
    confirmDeleteTasks: (taskIDs: number[]) => void;
    addMultipleTasks: (taskCount: number) => void;
    completeMultipleTasks: (taskIds: number[]) => void;
    getDependents: (taskID: number) => string[];
    isWorkingDay: (date: Date) => boolean;
    getWorkingDays: (from: Date, to: Date) => number;
    addWorkingDays: (date: Date, days: number) => Date;
    saveTaskOrder: (workPackageId: number) => void;
    indentTasksUnderParent: () => void;
    outdentTasksFromParent: () => void;
}

export const PlanBuilderContext = createContext<PlanBuilderContextProps>({
    updateProjectStartDate: () => { },
    projectStartDate: new Date(),
    projectLifecycleStage: "Initiation",
    workstreams: [],
    selectedWorkstreamID: null,
    selectedWorkpackageID: null,
    selectedTaskID: null,
    selectedTaskIDs: [],
    openWorkstreams: null,
    openWorkpackages: null,
    multiSelect: false,
    setOpenWorkstreams: () => { },
    selectWorkstream: () => { },
    selectWorkPackage: () => { },
    selectTask: () => { },
    selectMultiTask: () => { },
    setWorkstreams: () => { },
    addWorkstream: () => { },
    updateWorkstream: () => { },
    updateWorkPackage: () => { },
    deleteWorkstream: () => { },
    addWorkPackage: () => { },
    deleteWorkPackage: () => { },
    confirmDeleteWorkPackage: () => { },
    addTask: () => { },
    getTaskById: () => undefined,
    getWorkPackageById: () => undefined,
    getAllTasks: () => [],
    getDependentTaskIds: () => {},
    updateTask: () => { },
    groupEditTasks: () => { },
    toggleWorkstream: () => { },
    toggleWorkPackage: () => { },
    setShowEditForm: () => { },
    deleteTask: () => { },
    groupDeleteTasks: () => { },
    confirmDeleteTasks: () => { },
    addMultipleTasks: () => { },
    completeMultipleTasks: () => { },
    getDependents: () => [],
    isWorkingDay: () => false,
    addWorkingDays: () => new Date(),
    getWorkingDays: () => 0,
    saveTaskOrder: () => { },
    indentTasksUnderParent: () => {},
    outdentTasksFromParent: () => {},
});

export interface TaskGroupEdit {
    highlightedTaskId : number;
    taskIDs: number[];
    enableOwnerId: boolean;
    enableWorkPackage: boolean;
    enableIsDeliverable: boolean;
    enableIsMilestone: boolean;
    enableDisplayOnCharter: boolean;
    enableDisplayOnStatusReport: boolean;
    enableDuration: boolean;
    enableSetDependencies: boolean;
    enableSetSubtasks: boolean;
    ownerId: string;
    workstream: number;
    workPackage: number;
    isDeliverable: boolean;
    isMilestone: boolean;
    displayOnCharter: boolean;
    displayOnStatusReport: boolean;
    duration: number;
    setDependencies: "selected" | "dependents" | "cascade" | "remove";
    setSubtasks: boolean;
}